/**
 * GammaVision logo — the Aperture mark: a camera iris with Γ negative
 * space and a two-color gradient. Renders inline (gzips to well under
 * 1 KB) and is theme-independent.
 *
 * NOTE: the gradient stops below are the original handoff colors
 * (#3aff8a → #ffb000) and are intentionally left unchanged across both
 * themes per the brief (keep the logo + its two-color gradient). They
 * read as off-palette against the cobalt/plasma grounds — flagged, not
 * redesigned. Recoloring is a two-stop change if we later choose to.
 */

import React from 'react';

interface LogoProps {
  width?: number;
  height?: number;
  title?: string;
}

const Logo: React.FC<LogoProps> = ({ width = 42, height = 21, title }) => {
  // Aperture mark is square (1:1). It must stay bounded by the smaller
  // dimension so it never overflows its container — use min(width, height).
  // A 2:1 box (e.g. width=42 height=21) yields a 21×21 aperture, which
  // sits neatly inside the allocated space. Equal-dimension callers are unaffected.
  const apertureSize = Math.min(width, height);
  return (
    <span
      className="gv-logo"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
    >
      {/* Aperture (v2) ------------------------------------------------ */}
      <svg
        className="gv-logo-mark gv-logo-mark--aperture"
        viewBox="0 0 100 100"
        width={apertureSize}
        height={apertureSize}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="gv-aperture-grad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#3aff8a" />
            <stop offset="1" stopColor="#ffb000" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="44" fill="none" stroke="url(#gv-aperture-grad)" strokeWidth="3" />
        <path d="M 90.00 50.00 L 60.28 62.26 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.18" />
        <path d="M 70.00 84.64 L 41.43 65.99 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.31" />
        <path d="M 30.00 84.64 L 34.01 50.27 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.44" />
        <path d="M 10.00 50.00 L 39.72 37.74 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.57" />
        <path d="M 30.00 15.36 L 58.57 34.01 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.70" />
        <path d="M 70.00 15.36 L 65.99 49.73 L 50 50 Z" fill="url(#gv-aperture-grad)" opacity="0.83" />
        <path d="M 36 36 L 64 36 L 64 64" stroke="#000204" strokeWidth="6" fill="none" strokeLinecap="square" />
      </svg>

      {title && <span className="sr-only">{title}</span>}
    </span>
  );
};

export default Logo;
